import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { forexApi, type AccountCommand } from "@/lib/forex-api";

const phaseLabels: Record<string, string> = { pending: "待回报", submitted: "已提交", accepted: "已接收", rejected: "已拒绝", unknown: "待核对", final: "最终回报" };

export function AccountCommandList({ terminalId, accountScope, agentId }: { terminalId?: string; accountScope?: string; agentId?: string }) {
  const [commands, setCommands] = useState<AccountCommand[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    const request = accountScope || agentId
      ? forexApi.listAccountCommands(terminalId, null, { accountScope, agentId })
      : forexApi.listAccountCommands(terminalId);
    request
      .then((page) => { if (active) { setCommands(page.items); setCursor(page.next_cursor); } })
      .catch(() => { if (active) setError("账号命令暂不可用，请重试"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [terminalId, accountScope, agentId, retry]);
  const more = async () => {
    if (!cursor || loading) return;
    setLoading(true); setError("");
    try {
      const page = accountScope || agentId
        ? await forexApi.listAccountCommands(terminalId, cursor, { accountScope, agentId })
        : await forexApi.listAccountCommands(terminalId, cursor);
      setCommands((previous) => [...previous, ...page.items]); setCursor(page.next_cursor);
    } catch { setError("下一页账号命令加载失败，请重试"); }
    finally { setLoading(false); }
  };
  return <>
    {error && <div role="alert" className="text-sm text-red-600">{error} <button disabled={loading} onClick={() => cursor ? void more() : setRetry((value) => value + 1)}>重试</button></div>}
    {loading && <p>加载中...</p>}
    {!loading && !error && commands.length === 0 && <p>暂无账号命令</p>}
    {!!commands.length && <div className="overflow-x-auto"><p className="mb-2 text-xs text-text-muted"><span>已加载 {commands.length} 条</span>{cursor ? " · 仍有后续页" : ""}；命令状态不等于 Broker 成交</p>
      <table className="w-full text-sm"><thead><tr className="border-b text-left">
        <th className="p-3">命令 / 创建时间</th><th className="p-3">账号 / 终端</th><th className="p-3">操作 / 品种</th>
        <th className="p-3">请求 / 实际手数</th><th className="p-3">命令状态</th><th className="p-3">回报阶段 / 影响</th><th className="p-3">信号</th>
      </tr></thead><tbody>{commands.map((command) => <tr key={command.command_id} className="border-b">
        <td className="p-3 text-xs"><Link className="text-brand underline" to={`/forex/account-commands/detail?id=${encodeURIComponent(command.command_id)}`}>{command.command_id}</Link><div>{new Date(command.created_at * 1000).toLocaleString("zh-CN")}</div></td>
        <td className="p-3 text-xs">{command.account_scope}<div>{command.terminal_id}</div></td>
        <td className="p-3">{command.action} {command.side}<div>{command.symbol}</div></td>
        <td className="p-3">{command.requested_lot ?? "--"} / {command.actual_lot ?? "--"}
          {command.target_remaining_lot != null && <div>目标剩余 {command.target_remaining_lot}</div>}</td>
        <td className="p-3">{phaseLabels[command.status.toLowerCase()] || command.status}<div className="text-xs text-text-muted">{command.status}</div></td>
        <td className="p-3">{command.result_phase ?? "暂无回报"} / {command.order_effect ?? "未确认"}{command.result_phase && phaseLabels[command.result_phase.toLowerCase()] && <div className="text-xs text-text-muted">{phaseLabels[command.result_phase.toLowerCase()]}</div>}
          {command.error_code && <div>{command.error_code}</div>}</td>
        <td className="p-3"><Link className="text-brand underline" to={`/forex/execution-detail/detail?id=${encodeURIComponent(command.source_signal_id)}`}>查看共享信号</Link></td>
      </tr>)}</tbody></table></div>}
    {cursor && <button disabled={loading} onClick={() => void more()} className="rounded border px-3 py-2 text-sm">加载更多</button>}
  </>;
}

export default function AccountCommandTab() {
  const [params, setParams] = useSearchParams();
  const terminalId = params.get("terminal_id") || "";
  const accountScope = params.get("account_scope") || "";
  const agentId = params.get("agent_id") || "";
  const [filter, setFilter] = useState(terminalId);
  const [accountFilter, setAccountFilter] = useState(accountScope);
  const [agentFilter, setAgentFilter] = useState(agentId);
  return <div className="space-y-4">
    <h2 className="text-lg font-semibold">账号命令</h2>
    <p className="text-sm text-text-muted">主子账号独立执行。命令状态、执行回报和成交影响分别核对。</p>
    <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); const next: Record<string,string> = {}; if (filter.trim()) next.terminal_id = filter.trim(); if (accountFilter.trim()) next.account_scope = accountFilter.trim(); if (agentFilter.trim()) next.agent_id = agentFilter.trim(); setParams(next); }}>
      <input aria-label="终端筛选" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="终端编号" className="rounded border px-3 py-2" />
      <input aria-label="账号范围筛选" value={accountFilter} onChange={(event) => setAccountFilter(event.target.value)} placeholder="账号范围" className="rounded border px-3 py-2" />
      <input aria-label="智能体编号筛选" value={agentFilter} onChange={(event) => setAgentFilter(event.target.value)} placeholder="智能体编号" className="rounded border px-3 py-2" />
      <button type="submit" className="rounded border px-3 py-2">筛选</button>
    </form>
    <p className="text-xs text-text-muted">当前范围：终端 {terminalId || "全部"} · 账号 {accountScope || "全部"} · 智能体 {agentId || "全部"}</p>
    <AccountCommandList key={`${terminalId}:${accountScope}:${agentId}`} terminalId={terminalId || undefined} accountScope={accountScope || undefined} agentId={agentId || undefined} />
  </div>;
}
